import { useEffect, useMemo, useState } from "react";

import { useColorScheme, useTheme } from "@mui/material/styles";

import {
  getZebraRowColors,
  getZebraStripeBackground,
  isDarkColorScheme,
  zebraTableBodyCellSx,
} from "utils/colorUtils";
import { surfaceTableSx } from "utils/surfaceTheme";

/** Re-sync zebra colors after mobile browsers restore a backgrounded tab. */
function useColorSchemeRevision() {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const sync = () => setRevision((value) => value + 1);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener("focus", sync);
    return () => {
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return revision;
}

/** Shared alternating row colors for tables and mobile lists (light + dark). */
export default function useZebraRowColors() {
  const theme = useTheme();
  const { mode: colorSchemeMode } = useColorScheme();
  const colorSchemeRevision = useColorSchemeRevision();

  const isDarkUi = useMemo(
    () => isDarkColorScheme(theme, colorSchemeMode),
    [theme, colorSchemeMode, colorSchemeRevision],
  );
  const colors = useMemo(
    () => getZebraRowColors(theme, colorSchemeMode),
    [theme, colorSchemeMode, colorSchemeRevision],
  );
  const tableBodyCellSx = useMemo(
    () => zebraTableBodyCellSx(theme, colorSchemeMode),
    [theme, colorSchemeMode, colorSchemeRevision],
  );
  /** Devices data table: transparent header + primary borders + zebra body. */
  const tableSx = useMemo(
    () => surfaceTableSx(theme, colorSchemeMode, tableBodyCellSx),
    [theme, colorSchemeMode, tableBodyCellSx, colorSchemeRevision],
  );
  const stripeBg = useMemo(
    () => (rowIndex) => getZebraStripeBackground(rowIndex, theme, colorSchemeMode),
    [theme, colorSchemeMode, colorSchemeRevision],
  );

  return {
    theme,
    colorSchemeMode,
    isDarkUi,
    colors,
    tableBodyCellSx,
    tableSx,
    stripeBg,
  };
}
